import { useEffect, useState } from "react";
import {featuredArticleImage} from "../../../assets";

const FEATURED_CACHE_STORAGE_KEY = "featured_post_cache_v1";

const fallbackFeaturedArticle = {
  category: "Tips IELTS",
  date: "12 Januari 2025",
  title: "Strategi Lengkap Meningkatkan Skor IELTS dengan Metode Belajar Terstruktur",
  excerpt:
    "Banyak pelajar stuck di skor yang sama berbulan-bulan karena nggak punya sistem belajar yang jelas. Di artikel ini, kami breakdown semua strategi yang sudah terbukti membantu 500+ pelajar ScholarsToday tembus band 7+.",
  ctaLabel: "Baca selengkapnya",
  href: "/blog",
  image: featuredArticleImage,
};

const tickerItems = [
  "IELTS TIPS",
  "STUDY ABROAD",
  "CERITA SUKSES ALUMNI",
  "INFO BEASISWA 2026",
  "STUDY STRATEGY",
];

const normalizeWordPressBaseUrl = (value = "") => {
  const trimmed = value.trim().replace(/\/$/, "");

  if (!trimmed) {
    return "";
  }

  if (trimmed.includes("/wp-json")) {
    return trimmed;
  }

  return `${trimmed}/wp-json/wp/v2`;
};

const readJsonCache = (storageKey, fallbackValue) => {
  if (typeof window === "undefined") {
    return fallbackValue;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return fallbackValue;
    }

    return JSON.parse(rawValue);
  } catch {
    return fallbackValue;
  }
};

const writeJsonCache = (storageKey, value) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore storage quota or private mode failures.
  }
};

const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&#8217;/g, "'")
    .replace(/&#8220;/g, '"')
    .replace(/&#8221;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();

const getFirstHeadingFromContent = (content = "") => {
  const headingMatch = String(content).match(/<(h[1-6])\b[^>]*>([\s\S]*?)<\/\1>/i);

  if (!headingMatch) {
    return "";
  }

  return stripHtml(headingMatch[2]);
};

const formatDate = (value) => {
  if (!value) return "";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(parsed);
};

const getJakartaDateKey = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Jakarta",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((part) => part.type === "year")?.value || "";
  const month = parts.find((part) => part.type === "month")?.value || "";
  const day = parts.find((part) => part.type === "day")?.value || "";

  return `${year}-${month}-${day}`;
};

const readFeaturedArticleCache = () => {
  const cachedValue = readJsonCache(FEATURED_CACHE_STORAGE_KEY, null);
  const todayKey = getJakartaDateKey();

  if (!cachedValue || cachedValue.cacheDate !== todayKey || !cachedValue.article) {
    return null;
  }

  return cachedValue.article;
};

const hashString = (value = "") =>
  Array.from(value).reduce((accumulator, character) => accumulator + character.charCodeAt(0), 0);

const selectDailyPost = (posts, salt = "featured-section") => {
  if (!Array.isArray(posts) || posts.length === 0) {
    return null;
  }

  const index = hashString(`${getJakartaDateKey()}:${salt}`) % posts.length;

  return posts[index];
};

const mapWordPressFeaturedPost = (post) => ({
  category: post?._embedded?.["wp:term"]?.[0]?.[0]?.name || "Blog",
  date: formatDate(post?.date),
  title: stripHtml(post?.title?.rendered || "Untitled"),
  heroTitle: getFirstHeadingFromContent(post?.content?.rendered || "") || stripHtml(post?.title?.rendered || "Untitled"),
  excerpt: stripHtml(post?.excerpt?.rendered || post?.content?.rendered || "") || "Artikel ini belum memiliki ringkasan.",
  href: post?.link || "/blog",
  image: post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url || featuredArticleImage,
});

const Featured = () => {
  const cachedFeaturedArticle = readFeaturedArticleCache();

  const [featuredArticle, setFeaturedArticle] = useState(cachedFeaturedArticle || fallbackFeaturedArticle);
  const [isLoading, setIsLoading] = useState(!cachedFeaturedArticle);

  useEffect(() => {
    if (cachedFeaturedArticle) {
      return undefined;
    }

    const wordpressBaseUrl = normalizeWordPressBaseUrl(process.env.REACT_APP_WORDPRESS_API_URL);

    if (!wordpressBaseUrl) {
      setFeaturedArticle(fallbackFeaturedArticle);
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const loadFeaturedArticle = async () => {
      try {
        const queryParams = new URLSearchParams({
          _embed: "1",
          status: "publish",
          per_page: "12",
          orderby: "date",
          order: "desc",
        });

        const response = await fetch(`${wordpressBaseUrl}/posts?${queryParams.toString()}`, {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`WordPress request failed: ${response.status}`);
        }

        const data = await response.json();
        const publishedPosts = Array.isArray(data) ? data.filter((post) => post?.status === "publish") : [];
        const selectedPost = selectDailyPost(publishedPosts);

        if (selectedPost) {
          const mappedFeaturedArticle = mapWordPressFeaturedPost(selectedPost);

          setFeaturedArticle(mappedFeaturedArticle);
          writeJsonCache(FEATURED_CACHE_STORAGE_KEY, {
            cacheDate: getJakartaDateKey(),
            article: mappedFeaturedArticle,
          });
        } else {
          setFeaturedArticle(fallbackFeaturedArticle);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          setFeaturedArticle(fallbackFeaturedArticle);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadFeaturedArticle();

    return () => controller.abort();
  }, []);

  const renderSkeleton = () => (
    <article className="overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-[0_20px_40px_rgba(149,94,0,0.14)] lg:rounded-[30px]">
      <div className="grid min-h-[auto] lg:min-h-[500px] lg:grid-cols-2">
        <div className="relative min-h-[250px] overflow-hidden bg-[#f7ecd0] sm:min-h-[280px] lg:min-h-[500px]">
          <div className="h-full w-full animate-pulse bg-gradient-to-br from-[#f6e7ba] via-[#f4d98b] to-[#f9f0cf]" />
          <div className="absolute bottom-5 left-5 right-5 sm:bottom-6 sm:left-6 sm:right-6 lg:bottom-8 lg:left-8 lg:right-8">
            <div className="h-5 w-32 animate-pulse rounded-full bg-white/60 sm:h-6" />
            <div className="mt-4 h-8 w-full max-w-[18ch] animate-pulse rounded-full bg-white/60 sm:h-9 lg:h-10" />
            <div className="mt-3 h-8 w-4/5 max-w-[14ch] animate-pulse rounded-full bg-white/60 sm:h-9 lg:h-10" />
          </div>
        </div>

        <div className="flex flex-col justify-center px-5 py-6 sm:px-7 sm:py-7 lg:px-10 lg:py-10 xl:px-12">
          <div className="flex items-center gap-3 text-[12px] sm:text-[13px]">
            <div className="h-6 w-24 animate-pulse rounded-full bg-[#e9e3d1]" />
            <div className="h-4 w-28 animate-pulse rounded-full bg-[#e9e3d1]" />
          </div>

          <div className="mt-4 h-8 w-full max-w-[24ch] animate-pulse rounded-full bg-[#e9e3d1] sm:h-9 lg:mt-5 lg:h-10" />
          <div className="mt-3 h-8 w-11/12 max-w-[28ch] animate-pulse rounded-full bg-[#e9e3d1] sm:h-9 lg:h-10" />
          <div className="mt-3 h-8 w-10/12 max-w-[22ch] animate-pulse rounded-full bg-[#e9e3d1] sm:h-9 lg:h-10" />

          <div className="mt-4 space-y-3 lg:mt-5">
            <div className="h-4 w-full animate-pulse rounded-full bg-[#e9e3d1]" />
            <div className="h-4 w-11/12 animate-pulse rounded-full bg-[#e9e3d1]" />
            <div className="h-4 w-10/12 animate-pulse rounded-full bg-[#e9e3d1]" />
          </div>

          <div className="mt-6 h-11 w-36 animate-pulse rounded-[14px] bg-[#e9e3d1] sm:mt-7" />
        </div>
      </div>
    </article>
  );

  return (
    <section className="overflow-hidden bg-[#fdc83f] px-4 pt-10 pb-0 sm:px-6 sm:pt-12 sm:pb-0 lg:px-8 lg:pt-14 lg:pb-0">
      <div className="mx-auto w-full max-w-7xl">
        <div className="relative mx-auto mb-10 flex w-full max-w-[254px] items-center justify-center sm:mb-12">
          <div className="relative flex h-[64px] w-[184px] items-center justify-center rounded-full bg-[#fff8e9] shadow-[0_12px_28px_rgba(123,70,0,0.08)] sm:h-[76px] sm:w-[214px]">
            <span className="absolute left-5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-[var(--st-color-red)] sm:left-[34px]" />
            <span className="absolute right-5 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-[var(--st-color-red)] sm:right-[34px]" />
            <span className="text-[14px] font-bold uppercase leading-[1.1] tracking-[0.22em] text-[var(--st-color-red)] sm:text-[16px]">
              Artikel
              <br />
              Pilihan
            </span>
          </div>
        </div>

        {isLoading ? (
          renderSkeleton()
        ) : (
          <article className="overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-[0_20px_40px_rgba(149,94,0,0.14)] lg:rounded-[30px]">
            <div className="grid min-h-[auto] lg:min-h-[500px] lg:grid-cols-2">
              <div className="relative min-h-[250px] overflow-hidden sm:min-h-[280px] lg:min-h-[500px]">
                <img
                  src={featuredArticle.image}
                  alt={featuredArticle.title}
                  className="h-full w-full object-cover object-center"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/14 to-transparent lg:from-black/36 lg:via-black/10" />

                <div className="absolute bottom-5 left-5 right-5 sm:bottom-6 sm:left-6 sm:right-6 lg:bottom-8 lg:left-8 lg:right-8">
                  <h2 className="max-w-[24ch] text-[24px] font-semibold leading-[1.16] tracking-[-0.04em] text-white sm:text-[26px] lg:text-[32px]" style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: 3, overflow: "hidden" }}>
                    {featuredArticle.heroTitle}
                  </h2>
                </div>
              </div>

              <div className="flex flex-col justify-center px-5 py-6 sm:px-7 sm:py-7 lg:px-10 lg:py-10 xl:px-12">
                <div className="flex items-center gap-3 text-[12px] sm:text-[13px]">
                  <span className="inline-flex items-center rounded-full bg-[rgba(255,198,50,0.36)] px-3 py-1 font-semibold text-[#b36f00]">
                    {featuredArticle.category}
                  </span>
                  <span className="text-[var(--st-color-text-muted)]">{featuredArticle.date}</span>
                </div>

                <h3 className="mt-4 max-w-[20ch] text-[24px] font-semibold leading-[1.2] tracking-[-0.04em] text-[var(--st-color-heading)] sm:max-w-[32ch] sm:text-[26px] lg:mt-5 lg:text-[32px]">
                  {featuredArticle.title}
                </h3>

                <p
                  className="mt-4 max-w-[620px] text-[13px] leading-[1.7] text-[var(--st-color-text-muted)] sm:text-[14px] lg:mt-5 lg:text-[16px]"
                  style={{
                    display: "-webkit-box",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 3,
                    overflow: "hidden",
                  }}
                >
                  {featuredArticle.excerpt}
                </p>

                <div className="mt-6 sm:mt-7">
                  <a
                    href={featuredArticle.href}
                    className="inline-flex items-center justify-center rounded-[14px] bg-[var(--st-color-red)] px-5 py-3 text-[14px] font-semibold text-white shadow-[0_14px_26px_rgba(193,52,22,0.18)] transition-colors duration-200 hover:bg-[#a53317] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--st-color-navy)] focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                  >
                    Baca selengkapnya
                    <span className="ml-3 text-[16px]" aria-hidden="true">
                      →
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </article>
        )}
      </div>
      <div className="mt-10 -mx-4 overflow-hidden bg-[#b51f05] px-4 py-4 text-[#ffd042] sm:-mx-6 sm:px-6 sm:py-5 lg:mt-12 lg:-mx-8 lg:px-8 lg:py-5">
          <div className="whitespace-nowrap text-[18px] text-center font-semibold uppercase tracking-[0.08em] sm:text-[24px] lg:text-[34px]">
            {tickerItems.map((item, index) => (
              <span key={item}>
                {item}
                {index < tickerItems.length - 1 ? <span className="px-3">•</span> : null}
              </span>
            ))}
          </div>
        </div>
    </section>
  );
};

export default Featured;